import { MultiselectProps } from '@amzn/awsui-components-core/multiselect';
import { PNL_HIDDEN_REPORT_FILTER_DIMENSIONS } from 'src/components/star-grid/starGridConstants';
import { PnlReportConfig } from 'src/components/star-grid/starGridTypes';

export interface ReportFilterField {
  readonly key: string;
  readonly label: string;
  readonly options: MultiselectProps.Options;
  readonly selectedOptions: readonly MultiselectProps.Option[];
  readonly isAllSelected: boolean;
}

export const updateReportFilter = (
  filters: Readonly<Record<string, readonly string[]>>,
  field: string,
  options: readonly MultiselectProps.Option[],
  availableOptionCount: number
): Record<string, readonly string[]> => {
  const next = { ...filters };
  const values = [...new Set(options.flatMap((option) => (option.value === undefined ? [] : [option.value])))];
  if (values.length && values.length !== availableOptionCount) next[field] = values;
  else delete next[field];
  return next;
};

const filterLabel = (field: string, labels: Readonly<Record<string, string>>): string =>
  labels[field] ??
  field
    .replace(/^src_/, '')
    .replace(/_id$/, '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const buildReportFilterField = (
  key: string,
  definition: PnlReportConfig,
  filters: Readonly<Record<string, readonly string[]>>,
  labels: Readonly<Record<string, string>>
): ReportFilterField => {
  const selected = [...new Set(filters[key] ?? [])];
  const values = [...new Set([...(definition.dimension_values?.[key] ?? []), ...selected])].sort((a, b) => a.localeCompare(b));
  const options = values.map((value) => ({ value, label: value }));
  return {
    key,
    label: filterLabel(key, labels),
    options,
    selectedOptions: selected.length ? selected.map((value) => ({ value, label: value })) : options,
    isAllSelected: selected.length === 0 || selected.length === values.length
  };
};

export const buildReportFilterFields = (
  definition: PnlReportConfig | undefined,
  filters: Readonly<Record<string, readonly string[]>>,
  labels: Readonly<Record<string, string>>
): ReportFilterField[] => {
  if (!definition) return [];
  const dimensions = definition.filter_by_dimension.length ? definition.filter_by_dimension : definition.group_by_dimensions;
  const visibleDimensions = dimensions.filter((key) => !PNL_HIDDEN_REPORT_FILTER_DIMENSIONS.has(key));
  return [...new Set(visibleDimensions)].map((key) => buildReportFilterField(key, definition, filters, labels));
};
